import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { 
  TrendingUp, 
  Building2, 
  Factory, 
  Sprout, 
  Globe, 
  Handshake, 
  Mail, 
  Phone, 
  AlertTriangle,
  ArrowRight,
  Target,
  Users,
  Briefcase
} from 'lucide-react';

const DeveloppementPage = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const countries = [
    { name: 'Bénin', flag: '🇧🇯' },
    { name: "Côte d'Ivoire", flag: '🇨🇮' },
    { name: 'Sénégal', flag: '🇸🇳' },
    { name: 'Ghana', flag: '🇬🇭' },
    { name: 'Nigeria', flag: '🇳🇬' }
  ];

  const approachSteps = [
    { icon: Target, text: t('development.approach.identification') },
    { icon: TrendingUp, text: t('development.approach.analysis') },
    { icon: Handshake, text: t('development.approach.accompaniment') },
    { icon: Globe, text: t('development.approach.facilitation') }
  ];

  const targetCompanies = [
    t('development.target.structured'),
    t('development.target.industrial'),
    t('development.target.investment'),
    t('development.target.growth')
  ];

  const interventionAreas = [
    { icon: Factory, text: t('development.areas.manufacturing') },
    { icon: Sprout, text: t('development.areas.agro') },
    { icon: Building2, text: t('development.areas.industrialization') },
    { icon: TrendingUp, text: t('development.areas.modernization') },
    { icon: Globe, text: t('development.areas.export') }
  ];

  const ahbRoles = [
    t('development.role.detection'),
    t('development.role.accompaniment'),
    t('development.role.connection'),
    t('development.role.followup')
  ];

  const commitments = [
    t('development.commitment.competitiveness'),
    t('development.commitment.industrialization'),
    t('development.commitment.financing'),
    t('development.commitment.growth')
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[400px] mb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.15]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2px, transparent 2px),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 1.5px, transparent 1.5px),
                radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 2.5px, transparent 2.5px)
              `,
              backgroundSize: '120px 120px, 100px 100px, 140px 140px, 110px 110px'
            }}></div>
          </div>

          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
              <div className="inline-flex items-center gap-2 mb-6">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                  <Globe className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-medium text-white/90 uppercase tracking-widest">
                  {t('development.hero.subtitle')}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight drop-shadow-lg mb-6 leading-tight">
                {t('development.hero.title')}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-sm">
                {t('development.hero.description')}
              </p>
              <div className="w-32 h-1 bg-white/60 mx-auto mt-8 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#089556]" />
            {t('development.countries.title')}
          </h2>
          <div className="flex flex-wrap gap-3">
            {countries.map((country) => (
              <div 
                key={country.name}
                className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full border border-gray-100"
              >
                <span className="text-lg">{country.flag}</span>
                <span className="text-sm font-medium text-gray-700">{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="bg-gradient-to-br from-[#089556]/5 to-[#07D1EB]/5 rounded-xl p-4 sm:p-6 border border-[#089556]/10">
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            {t('development.mission.text')}
          </p>
        </div>
      </section>

      {/* Approach Section */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-[#089556]" />
          {t('development.approach.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {approachSteps.map((step, index) => (
            <div 
              key={index}
              className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex items-start gap-3"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center">
                <step.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm text-gray-700 leading-snug pt-1">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two Column Layout */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Target Companies */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#089556]" />
              {t('development.target.title')}
            </h2>
            <ul className="space-y-3">
              {targetCompanies.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-[#089556]/10 rounded-full flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-[#089556] rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Intervention Areas */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#089556]" />
              {t('development.areas.title')}
            </h2>
            <div className="space-y-3">
              {interventionAreas.map((area, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-[#089556]/10 to-[#07D1EB]/10 rounded-lg flex items-center justify-center">
                    <area.icon className="w-4 h-4 text-[#089556]" />
                  </div>
                  <span className="text-sm text-gray-700">{area.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AHB Role Section */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-xl p-4 sm:p-6 text-white">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Handshake className="w-5 h-5" />
            {t('development.role.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ahbRoles.map((role, index) => (
              <div key={index} className="flex items-start gap-3 bg-white/10 rounded-lg p-3">
                <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/80" />
                <span className="text-sm text-white/95">{role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#089556]" />
            {t('development.commitment.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {commitments.map((commitment, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-[#089556] rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-gray-700">{commitment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6 text-white">
          <h2 className="text-lg font-bold mb-4">{t('development.contact.title')}</h2>
          <p className="text-sm text-gray-300 mb-4">
            {t('development.contact.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:info@ahbenin.org"
              className="flex items-center gap-2 text-sm text-white hover:text-[#07D1EB] transition-colors"
            >
              <Mail className="w-4 h-4" />
              info@ahbenin.org
            </a>
            <a 
              href="tel:+2290155078181"
              className="flex items-center gap-2 text-sm text-white hover:text-[#07D1EB] transition-colors"
            >
              <Phone className="w-4 h-4" />
              +229 0155078181
            </a>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="max-w-6xl mx-auto px-4 pb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-amber-800 mb-1">
                {t('development.notice.title')}
              </h3>
              <p className="text-sm text-amber-700 leading-relaxed">
                {t('development.notice.text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-6xl mx-auto px-4 pb-8">
        <div className="text-center">
          <button
            onClick={() => navigate(`/${currentLanguage}/contact`)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            {t('development.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeveloppementPage;
